import { useEffect, useState } from "react";
import StartingOptions from "./StartingOptions";
import DeletingPostConfirmation from "./DeletingPostConfirmation";
import UnsuccessfulDeletingPost from "./UnsuccessfulDeletingPost";
import PostDeletedSuccessfully from "./PostDeletedSuccessfully";
import SharingContent from "../ShareOptionsModal/SharingContent";
import SharingLinkCopiedSuccessfully from "../ShareOptionsModal/SharingLinkCopiedSuccessfully";
import UnsuccessfulCopyingSharingLink from "../ShareOptionsModal/UnsuccessfulCopyingSharingLink";
import UnsuccessfulShorteningSharingLink from "../ShareOptionsModal/UnsuccessfulShorteningSharingLink";
import { SERVICE_MODALS_OPTIONS_STATES } from "../../ServiceFunctionsModal.types";
import { isDeviceOutputMobileOrMobileApp } from "../../../../../Domain/DeviceOutputState";
import ReportingConfirmation from "../ReportOptionsModal/ReportingConfirmation";
import {
    getReportReasons,
    reportPost,
} from "@infrastructure/Services/ReportService";
import {
    ReportClassType,
    ReportReason,
} from "@infrastructure/Services/ReportService.types";
import ReportedSuccessfully from "../ReportOptionsModal/ReportedSuccessfully";
import { ResourceSuspended } from "../GeneralErrorsModal/ResourceSuspended";
import {
    RESPONSE_CODE_LIMIT_EXCEEDED,
    RESPONSE_CODE_NOT_AUTHORIZED,
    RESPONSE_CODE_PRIVACY_POLICY_UNACCEPTED,
    RESPONSE_CODE_PROFILE_DEACTIVATED,
    RESPONSE_CODE_RESOURCE_NOT_FOUND,
    RESPONSE_CODE_RESOURCE_SUSPENDED,
} from "@infrastructure/BackendClientCodes";
import UnsuccessfulReporting from "../ReportOptionsModal/UnsuccessfulReporting";
import {
    useInterruptableAction,
    UserLoginActionState,
    useUserStore,
} from "@infrastructure/Persistence/UserState";
import { usePostDetailsNavigationActions } from "@infrastructure/Hooks/usePostDetailsNavigationActions";
import { PostOptionsModalStateRet } from "./PostOptionsModalState.types";
import { useIsCurrentUserId } from "@infrastructure/Hooks/UserInteractions/useUserId";
import {
    RESET_API_CACHE_REASONS,
    resetIgnoreApiCacheTime,
} from "@infrastructure/Services/ApiUtility";
import { publish } from "@infrastructure/Events/EventBus";
import { POST_DELETED, POST_REPORTED } from "../../../Post/PostForm/events";
import { useGroupSelector } from "../../../GroupSelector/useGroupSelector";

const FIFTEEN_MINUTES = 15 * 60 * 1000;

export default function PostOptionsModalState({
    selectedPost,
    setOpen,
    onEditOptionClickHandler,
    setModalCurrentAction,
    modalCurrentAction,
    handleCommentInputFocus,
}: any): PostOptionsModalStateRet | undefined {
    const isMobileOrMobileApp = isDeviceOutputMobileOrMobileApp();
    const [shortLink, setShortLink] = useState("");
    const [reportReasons, setReportReasons] = useState([] as ReportReason[]);
    const [isLoading, setIsLoading] = useState(true);
    const [showReportOptionsErrorLabel, setShowReportOptionsErrorLabel] =
        useState(false);

    const { mainGroupId: groupId } = useGroupSelector();

    const selectedPostData = selectedPost.data;

    const loadReportReasons = function () {
        getReportReasons(ReportClassType.CONTENT)
            .then((reportReasons) => {
                setReportReasons(reportReasons);
                setIsLoading(false);
            })
            .catch(() => {
                setModalCurrentAction(
                    SERVICE_MODALS_OPTIONS_STATES.UNSUCCESSFUL_CONTENT_REPORT,
                );
            });
    };

    useEffect(() => {
        if (
            modalCurrentAction ==
            SERVICE_MODALS_OPTIONS_STATES.REPORTING_CONTENT
        ) {
            loadReportReasons();
        }
    }, [modalCurrentAction]);

    const [isEditAvailable, setIsEditAvailable] = useState(true);
    useEffect(() => {
        const currentTime = new Date().getTime();
        const itemCreationTime = new Date(
            selectedPostData?.postData.createdAt,
        ).getTime();
        const timeDifference = currentTime - itemCreationTime;

        if (timeDifference >= FIFTEEN_MINUTES) {
            setIsEditAvailable(false);
        } else {
            setIsEditAvailable(true);
            const timer = setTimeout(() => {
                setIsEditAvailable(false);
            }, FIFTEEN_MINUTES - timeDifference);

            return () => {
                clearTimeout(timer);
            };
        }
    }, [selectedPost]);
    const { setUserAuthContextState } = useUserStore();
    const [isReporting, setIsReporting] = useState(false);
    const [reasonId, setReasonId] = useState<number | undefined>();
    const isOwnPost = useIsCurrentUserId(selectedPostData?.user.uuid);

    useEffect(() => {
        if (reasonId && isReporting) {
            setIsLoading(true);
            handleReportSubmit();
        }
    }, [reasonId, isReporting]);

    const cancelIsReporting = () => {
        setIsReporting(false);
    };

    useInterruptableAction(isReporting, handleReportSubmit, cancelIsReporting);

    function handleReportSubmit() {
        if (!reasonId) {
            setShowReportOptionsErrorLabel(true);
        } else {
            setIsLoading(true);
            resetIgnoreApiCacheTime(RESET_API_CACHE_REASONS.POST_REPORT);
            setShowReportOptionsErrorLabel(false);
            reportPost(reasonId.toString(), selectedPostData.id)
                .then(() => {
                    setModalCurrentAction(
                        SERVICE_MODALS_OPTIONS_STATES.SUCCESSFUL_CONTENT_REPORT,
                    );
                    setReasonId(undefined);
                    publish({
                        name: POST_REPORTED,
                        data: { postId: selectedPostData.id },
                    });
                })
                .catch((reason) => {
                    setIsLoading(false);
                    if (
                        reason.response?.status ===
                            RESPONSE_CODE_RESOURCE_SUSPENDED ||
                        reason.response?.status ===
                            RESPONSE_CODE_RESOURCE_NOT_FOUND
                    ) {
                        setModalCurrentAction(
                            SERVICE_MODALS_OPTIONS_STATES.REPORTED_CONTENT_RESTRICTED,
                        );
                    } else if (
                        reason.response?.status &&
                        ![
                            RESPONSE_CODE_PROFILE_DEACTIVATED,
                            RESPONSE_CODE_NOT_AUTHORIZED,
                            RESPONSE_CODE_PRIVACY_POLICY_UNACCEPTED,
                            RESPONSE_CODE_LIMIT_EXCEEDED,
                        ].includes(reason.response?.status)
                    ) {
                        setModalCurrentAction(
                            SERVICE_MODALS_OPTIONS_STATES.UNSUCCESSFUL_CONTENT_REPORT,
                        );
                    } else if (
                        reason.response?.status === RESPONSE_CODE_LIMIT_EXCEEDED
                    ) {
                        setOpen(false);
                    }
                });
        }
    }

    function onSelectedPostSuccessDeleteHandler() {
        publish({ name: POST_DELETED, data: { postId: selectedPostData.id } });
    }

    function onReportConfirmation(selectedId: number) {
        setIsReporting(true);
        setUserAuthContextState(UserLoginActionState.REPORT_CONTENT);
        setReasonId(selectedId);
    }

    const { navigateToPostWithAction } = usePostDetailsNavigationActions();

    switch (modalCurrentAction) {
        case SERVICE_MODALS_OPTIONS_STATES.DELETING_POST: {
            return DeletingPostConfirmation(
                setModalCurrentAction,
                selectedPostData,
                onSelectedPostSuccessDeleteHandler,
            );
        }
        case SERVICE_MODALS_OPTIONS_STATES.DELETED_POST: {
            return {
                ...PostDeletedSuccessfully(),
            };
        }
        case SERVICE_MODALS_OPTIONS_STATES.UNSUCCESSFUL_DELETING_POST: {
            return UnsuccessfulDeletingPost();
        }
        case SERVICE_MODALS_OPTIONS_STATES.SHARING_POST: {
            const groupName = selectedPostData.group.name ?? "Unbekannt";
            if (!groupId) {
                return;
            }
            return SharingContent(
                groupId,
                selectedPost.id,
                shortLink,
                setShortLink,
                groupName,
                isMobileOrMobileApp,
                setModalCurrentAction,
            );
        }
        case SERVICE_MODALS_OPTIONS_STATES.SHARING_LINK_COPIED_SUCCESSFULLY: {
            return SharingLinkCopiedSuccessfully();
        }
        case SERVICE_MODALS_OPTIONS_STATES.UNSUCCESSFUL_COPYING_SHARING_LINK: {
            return UnsuccessfulCopyingSharingLink();
        }
        case SERVICE_MODALS_OPTIONS_STATES.UNSUCCESSFUL_SHORTENING_SHARING_LINK: {
            return UnsuccessfulShorteningSharingLink();
        }
        case SERVICE_MODALS_OPTIONS_STATES.REPORTING_CONTENT: {
            return ReportingConfirmation({
                reportReasons,
                isLoading,
                showReportOptionsErrorLabel,
                onReportConfirmation,
            });
        }
        case SERVICE_MODALS_OPTIONS_STATES.SUCCESSFUL_CONTENT_REPORT: {
            return ReportedSuccessfully();
        }
        case SERVICE_MODALS_OPTIONS_STATES.UNSUCCESSFUL_CONTENT_REPORT: {
            return UnsuccessfulReporting();
        }
        case SERVICE_MODALS_OPTIONS_STATES.REPORTED_CONTENT_RESTRICTED: {
            return ResourceSuspended();
        }
        case SERVICE_MODALS_OPTIONS_STATES.POST_STARTING_OPTIONS: {
            return StartingOptions({
                selectedPost,
                setOpen,
                onEditOptionClickHandler,
                setModalCurrentAction,
                showDesktopSharingOptions: !isMobileOrMobileApp,
                handleCommentInputFocus,
                isEditAvailable,
                navigateToPostWithAction,
                isOwnPost,
                groupId,
            });
        }
    }
}
